import React, { useCallback, useState } from 'react';
import { useFormik } from 'formik';
import { TextField, Button, FormLayout, Layout, Card, Page, Text, BlockStack, Toast } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import { getAllContactUs } from '../redux/contactUs/slice';
import { contactUsAsync } from '../redux/contactUs';
import { getAllUserOnboarding } from '../redux/userOnboarding/slice';
import useOnboardingRedirect from '../redux/userOnBoardingRedirect';
import Placeholder from '../components/common/Placeholder';
import AppBridgeSavebar from '../components/common/AppBridgeSavebar';
import { validationSchema, isAdmin } from '../utils';

const ContactUsPage = () => {
    const dispatch = useDispatch();
    const { isContactUsLoading } = useSelector(getAllContactUs);
    const { userOnboardingData } = useSelector(getAllUserOnboarding);
    const [formData, setFormData] = useState({
        email: '',
        phone: '',
        message: ''
    });
    const [active, setActive] = useState(false);
    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);

    useOnboardingRedirect(userOnboardingData);

    const toggleActive = useCallback((message) => {
        setMessage(message);
        setActive((active) => !active);
    }, []);

    const toastMarkup = active ? (
        <Toast duration={2000} content={message} error={isSuccess} onDismiss={toggleActive} />
    ) : null;

    const formik = useFormik({
        initialValues: formData,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => handleSubmit(values)
    });

    const handleSubmit = async () => {
        // e.preventDefault();
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
            formik.setTouched({
                email: true,
                phone: true,
                message: true
            });
            toggleActive('Please fix the form errors before submitting.');
            setIsSuccess(true);
            return;
        }
        setIsSuccess(false);
        dispatch(contactUsAsync({
            payload: formik.values,
            callback: handleCallBack
        }));
    };

    const handleCallBack = (success, message) => {
        if (success) {
            toggleActive(message);
            handleReset();
        }
    };

    const handleReset = () => {
        formik.resetForm();
        setFormData({
            email: '',
            phone: '',
            message: ''
        });
    };

    const customTitle = (
        <BlockStack gap="200">
            <Text variant="headingLg" as="h2">Contact Us for Live Gold Rate Support</Text>
            <Text variant="bodyMd">
                For issues with live gold rates or inquiries, fill out the form below and our support team will assist you.
            </Text>
        </BlockStack>
    );

    return (
        <Page title={customTitle}>
            {isAdmin &&
                <AppBridgeSavebar
                    saveAction={{ disabled: !formik.values.email.trim() || !formik.values.phone.trim() || !formik.values.message.trim(), loading: isContactUsLoading, onAction: handleSubmit }}
                    discardAction={{ onAction: handleReset }}
                    visible={formik.values.email || formik.values.phone || formik.values.message}
                />
            }
            <Layout>
                <Layout.Section>
                    <Card>
                        <FormLayout>
                            <TextField
                                label="Email Address"
                                type="email"
                                name="email"
                                value={formik.values.email}
                                onChange={(value) => { formik.setFieldValue('email', value) }}
                                onBlur={() => formik.setFieldTouched('email')}
                                error={formik.touched.email && formik.errors.email ? formik.errors.email : ''}
                                placeholder="Enter your Email Address"
                            />
                            <TextField
                                label="Phone Number"
                                name="phone"
                                value={formik.values.phone}
                                onChange={(value) => { formik.setFieldValue('phone', value) }}
                                onBlur={() => formik.setFieldTouched('phone')}
                                error={formik.touched.phone && formik.errors.phone ? formik.errors.phone : ''}
                                placeholder="Enter your Phone Number"
                            />
                            <TextField
                                label="Query"
                                name="query"
                                placeholder='Write your query here'
                                value={formik.values.message}
                                onChange={(value) => { formik.setFieldValue('message', value) }}
                                onBlur={() => formik.setFieldTouched('message')}
                                error={formik.touched.message && formik.errors.message ? formik.errors.message : ''}
                                helpText="Please describe any issues with live gold rate updates or pricing discrepancies."
                                multiline={4}
                            />
                            {!isAdmin && <Button disabled={
                                !formik.values.email.trim() ||
                                !formik.values.phone.trim() ||
                                !formik.values.message.trim()
                            } loading={isContactUsLoading} variant="primary" onClick={handleSubmit}>
                                Submit
                            </Button>}
                        </FormLayout>
                    </Card>
                </Layout.Section>
            </Layout>
            <Placeholder />
            {toastMarkup}
        </Page>
    );
};

export default ContactUsPage;